import superFetch from '../utils/super-fetch';
import authService from './auth-service';
/* global CONFIG */
const { apiUrl } = CONFIG;

const buildApiError = (detail, extras = {}) => Object.assign(new Error(detail), {
  detail,
  ...extras,
});

const parseErrorBody = (err) => {
  if (err && typeof err.json === 'function') {
    return err.json().catch(() => null);
  }

  if (err && typeof err === 'object') {
    return Promise.resolve(err);
  }

  return Promise.resolve(null);
};

const toApiError = (err) => {
  if (!err) {
    return Promise.reject(buildApiError('Unable to reach server. Please try again.'));
  }

  return parseErrorBody(err).then(
    (body) => {
      const detail = body?.detail || body?.error || body?.message || err.message || err.statusText || 'Request failed.';
      return Promise.reject(buildApiError(detail, {
        status: err.status,
        ...(body && typeof body === 'object' ? body : {}),
      }));
    },
    () => Promise.reject(buildApiError(err.message || err.statusText || 'Request failed.', {
      status: err.status,
    })),
  );
};

const getVerificationRequest = (dealId) => {
  const url = `${apiUrl}/voucher-verify/${dealId}`;
  return superFetch(url, {
    method: 'GET',
    headers: { Authorization: `JWT ${authService.getToken()}` },
  }).then(res => res.json())
    .catch((err) => {
      // 410 = already verified; parse the response body when present and let the caller handle it normally
      if (err && err.status === 410) {
        return parseErrorBody(err).then((body) => {
          if (body && typeof body === 'object' && !Array.isArray(body)) {
            return body;
          }

          return Promise.reject(buildApiError(
            err.message || err.statusText || 'Already verified.',
            {
              status: err.status,
            },
          ));
        });
      }
      return toApiError(err);
    });
};

const saveVerification = (dealId, payload) => {
  const url = `${apiUrl}/voucher-verify/${dealId}/save`;
  return superFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `JWT ${authService.getToken()}`,
    },
  }, JSON.stringify(payload))
    .then(res => res.json())
    .catch(toApiError);
};

const submitVerification = (dealId, payload) => {
  const url = `${apiUrl}/voucher-verify/${dealId}/verify`;
  return superFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `JWT ${authService.getToken()}`,
    },
  }, JSON.stringify(payload))
    .then(res => res.json())
    .catch(toApiError);
};

const fetchVerificationAttachments = (dealId) => {
  const url = `${apiUrl}/mbb/attachments`;
  const params = {
    deal: dealId,
    ordering: '-modified',
    page: 1,
    page_size: 100,
  };
  return superFetch(url, {
    method: 'GET',
    params,
    headers: { Authorization: `JWT ${authService.getToken()}` },
  }).then(res => res.json())
    .then((data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    })
    .catch(toApiError);
};

const uploadVerificationAttachment = (dealId, file, documentType) => {
  const url = `${apiUrl}/mbb/attachments_upload`;
  const formData = new FormData();
  const filename = file?.name || 'verification-document';
  const dotIndex = filename.lastIndexOf('.');
  const title = dotIndex > -1 ? filename.substring(0, dotIndex) : filename;

  formData.append('is_lease_document', 'True');
  formData.append('deal_id', parseInt(dealId, 10));
  formData.append('file_size', parseInt(file?.size || 0, 10));
  formData.append('title', title);
  formData.append('document_name', filename);
  formData.append('privacy', 'team');
  formData.append('file_type', file?.type || 'application/octet-stream');
  formData.append('file_content', file);
  formData.append('extraction_opted_in', 'False');
  // document_category is sent so the backend can skip Lease Abstraction auto-extraction for
  // this file, but the backend intentionally does NOT persist it on the Attachment record at
  // upload time -- Save/Verify's own _sync_commission_attachment_flags is the sole place that
  // assigns it, so the before/after attachment diff used for verification audit entries isn't
  // pre-mutated by the upload itself.
  formData.append('document_category', documentType);

  return superFetch(url, {
    method: 'POST',
    headers: {
      Authorization: `JWT ${authService.getToken()}`,
    },
  }, formData)
    .then(res => res.json())
    .then((data) => {
      if (data?.attachments?.length) return data.attachments[0];
      return data;
    })
    .catch(toApiError);
};

const getVerificationBounds = () => {
  const url = `${apiUrl}/broker-verification/bounds`;
  return superFetch(url, {
    method: 'GET',
    headers: { Authorization: `JWT ${authService.getToken()}` },
  })
    .then(res => res.json())
    .catch(toApiError);
};

const verificationService = {
  getVerificationRequest,
  saveVerification,
  submitVerification,
  fetchVerificationAttachments,
  uploadVerificationAttachment,
  getVerificationBounds,
};

export default verificationService;
